<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import JTabs from '@/components/molecules/JTabs.vue'
import { cn } from '@/lib/utils'
import type {
  JDynamicTabsProps, 
  JDynamicTabsEmits, 
  JDynamicTabsMethods,
  DynamicTab 
} from '@/types/dynamic-tabs.types'

/**
 * JDynamicTabs - 동적 탭 컴포넌트 (organisms)
 * Dynamic Tabs Component
 * 
 * @description
 * 사이드 메뉴 클릭으로 탭을 동적으로 추가/제거할 수 있는 탭 컴포넌트입니다.
 * 
 * Features:
 * - 탭 동적 추가/제거
 * - 중복 탭 방지 (이미 있으면 활성화)
 * - 닫기 버튼으로 탭 제거
 * - 활성 탭 자동 재설정
 * - 최대 탭 개수 제한
 * 
 * @example
 * const tabsRef = ref()
 * 
 * const handleMenuClick = (menuItem) => {
 *   tabsRef.value?.addTab({
 *     id: menuItem.id,
 *     label: menuItem.label,
 *     component: menuItem.component,
 *     closable: true
 *   })
 * }
 */

const props = withDefaults(defineProps<JDynamicTabsProps>(), {
  initialTabs: () => [],
  maxTabs: 0, // 0 = unlimited
  emptyMessage: '탭을 추가해주세요.',
  styletype: 'default',
})

const emit = defineEmits<JDynamicTabsEmits>()

/**
 * 탭 목록 (내부 상태)
 * Tabs list (internal state)
 */
const tabs = ref<DynamicTab[]>(Array.isArray(props.initialTabs) ? [...props.initialTabs] : [])

/**
 * 현재 활성화된 탭 ID (내부 상태)
 * Current active tab ID (internal state)
 */
const activeTabId = ref<string>(
  props.defaultActiveId || (Array.isArray(props.initialTabs) && props.initialTabs.length > 0 ? props.initialTabs[0]?.id : '') || ''
)

/**
 * initialTabs가 변경되면 내부 상태 업데이트
 * Update internal state when initialTabs changes
 */
watch(() => props.initialTabs, (newTabs) => {
  if (!newTabs) {
    tabs.value = []
    return
  }
  
  if (Array.isArray(newTabs) && newTabs.length > 0) {
    tabs.value = [...newTabs]
    if (!activeTabId.value && newTabs[0]) {
      activeTabId.value = newTabs[0].id
    }
  } else if (Array.isArray(newTabs)) {
    // 빈 배열인 경우
    tabs.value = []
  } else {
    // 배열이 아닌 경우
    tabs.value = []
  }
}, { immediate: true })

/**
 * 탭 추가
 * Add tab (if exists, activate it; otherwise, add to array and activate)
 * 
 * @param tab - 추가할 탭 정보
 */
const addTab = (tab: DynamicTab) => {
  // tabs.value가 배열이 아니면 초기화
  if (!Array.isArray(tabs.value)) {
    tabs.value = []
  }
  
  // 이미 존재하는 탭인지 확인
  const existingTab = tabs.value.find(t => t.id === tab.id)
  
  if (existingTab) {
    // 이미 있으면 해당 탭 활성화
    activateTab(tab.id)
    return
  }
  
  // 최대 탭 개수 체크
  if (props.maxTabs > 0 && tabs.value.length >= props.maxTabs) {
    console.warn(`최대 ${props.maxTabs}개의 탭만 열 수 있습니다.`)
    return
  }
  
  // 새 탭 추가
  tabs.value.push({
    ...tab,
    closable: tab.closable !== false, // 기본값 true
  })
  
  // 새로 추가된 탭 활성화
  activateTab(tab.id)
  
  // 이벤트 발생
  emit('tabAdd', tab)
}

/**
 * 탭 닫기
 * Close tab (remove from array and reset active tab)
 * 
 * @param id - 닫을 탭 ID
 */
const closeTab = (id: string) => {
  if (!Array.isArray(tabs.value)) {
    return
  }
  
  const tabIndex = tabs.value.findIndex(t => t.id === id)
  
  if (tabIndex === -1) return
  
  // 탭 제거
  tabs.value.splice(tabIndex, 1)
  
  // 활성 탭 재설정
  if (activeTabId.value === id) {
    if (tabs.value.length > 0) {
      // 이전 탭이 있으면 이전 탭 활성화, 없으면 다음 탭 활성화
      const newIndex = Math.min(tabIndex, tabs.value.length - 1)
      activeTabId.value = tabs.value[newIndex]?.id || ''
    } else {
      activeTabId.value = ''
    }
  }
  
  // 이벤트 발생
  emit('tabClose', id)
}

/**
 * 탭 활성화
 * Activate tab
 * 
 * @param id - 활성화할 탭 ID
 */
const activateTab = (id: string) => {
  if (!Array.isArray(tabs.value)) {
    return
  }
  const tab = tabs.value.find(t => t.id === id)
  if (tab) {
    activeTabId.value = id
  }
}

/**
 * 특정 탭 찾기
 * Find specific tab
 * 
 * @param id - 찾을 탭 ID
 */
const findTab = (id: string): DynamicTab | undefined => {
  if (!Array.isArray(tabs.value)) {
    return undefined
  }
  return tabs.value.find(t => t.id === id)
}

/**
 * 모든 탭 닫기 (closable이 true인 탭만)
 * Close all tabs (only closable tabs)
 */
const closeAllTabs = () => {
  if (!Array.isArray(tabs.value)) {
    return
  }
  const closableTabs = tabs.value.filter(t => t.closable)
  closableTabs.forEach(tab => closeTab(tab.id))
}

/**
 * 탭 변경 핸들러
 * Tab change handler
 */
const handleTabChange = (id: string) => {
  activeTabId.value = id
  emit('tabChange', id)
}

/**
 * 탭 닫기 핸들러
 * Tab close handler
 */
const handleTabClose = (id: string) => {
  closeTab(id)
}

/**
 * 외부에서 호출 가능한 메서드 노출
 * Expose methods for external use
 */
defineExpose<JDynamicTabsMethods>({
  addTab,
  closeTab,
  activateTab,
  findTab,
  closeAllTabs,
})

/**
 * 안전한 tabs 배열
 * Safe tabs array
 */
const safeTabs = computed(() => {
  return Array.isArray(tabs.value) ? tabs.value : []
})

/**
 * 탭이 있는지 여부
 * Whether there are tabs
 */
const hasTabs = computed(() => safeTabs.value.length > 0)

/**
 * 루트 클래스
 * Root classes
 */
const rootClasses = computed(() => {
  return cn('w-full h-full', props.class)
})

/**
 * 콘텐츠 클래스
 * Content classes
 */
const contentClasses = computed(() => {
  return props.contentClass || ''
})
</script>

<template>
  <div :class="rootClasses">
    <!-- 탭이 있을 경우 / When there are tabs -->
    <JTabs
      v-if="hasTabs"
      :tabs="safeTabs"
      :active-tab-id="activeTabId"
      :styletype="props.styletype"
      :keep-alive="props.keepAlive"
      :class="contentClasses"
      @tab-change="handleTabChange"
      @tab-close="handleTabClose"
    >
      <!-- 각 탭 콘텐츠 슬롯 전달 / Forward slots for each tab content -->
      <template
        v-for="tab in safeTabs"
        :key="tab.id"
        #[`content-${tab.id}`]="slotProps"
      >
        <slot :name="`content-${tab.id}`" v-bind="slotProps" />
      </template>
    </JTabs>
    
    <!-- 탭이 없을 경우 / When there are no tabs -->
    <div
      v-else
      class="flex items-center justify-center h-full w-full"
    >
      <div class="text-center">
        <p class="text-muted-foreground text-lg">{{ emptyMessage }}</p>
        <p class="text-muted-foreground/60 text-sm mt-2">
          메뉴를 클릭하여 탭을 추가하세요.
        </p>
      </div>
    </div>
  </div>
</template>
